import { Injectable } from '@angular/core';
import { Observable, shareReplay, Subscriber, tap } from 'rxjs';

import { environment } from '@env/environment';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { GTMEventType } from '@app/models';

interface EmitGTMEventOptions {
  signup_origin?: 'homepage' | 'invite' | 'member';
  plan_type?: string;
  billing_cycle?: 'annual' | 'monthly' | 'carnival';
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService extends Angulartics2GoogleTagManager {
  private _isBooted = false;

  super() {}

  get isBooted() {
    return this._isBooted;
  }

  boot() {
    return new Observable<void>(subscriber => {
      if (!environment.googleTagManagerId) {
        this.finalizeBoot(subscriber);
        return;
      }

      const scriptId = 'gtm-script';

      if (document.getElementById(scriptId)) {
        this.finalizeBoot(subscriber);
        return;
      }

      try {
        const head = document.getElementsByTagName('head')[0];

        const gtmScript = this.createGTMScriptElement(scriptId, subscriber);
        head.appendChild(gtmScript);
      } catch (error) {
        subscriber.error(error);
      }
    }).pipe(
      tap(() => (this._isBooted = true)),
      shareReplay(1)
    );
  }

  startTracking() {
    if (!this.isBooted) {
      this.boot().subscribe({
        next: () => super.startTracking()
      });
    } else {
      super.startTracking();
    }
  }

  private finalizeBoot(subscriber: Subscriber<void>) {
    this._isBooted = true;
    subscriber.next();
    subscriber.complete();
  }

  private createGTMScriptElement(id: string, subscriber: Subscriber<void>) {
    const script = document.createElement('script');
    script.id = id;
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${environment.googleTagManagerId}');`;

    setTimeout(() => {
      subscriber.next();
      subscriber.complete();
    });

    return script;
  }

  emitGTMEvent(event: GTMEventType, user_id: string, options?: EmitGTMEventOptions) {
    if (!environment.production) return;

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, user_id, ...options });
    } catch (e) {}
  }
}
